import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useRecoilState } from 'recoil'
import * as xlsx from 'xlsx'

import vendorAtom from '@src/atom/vendor'
import { formatPriceByLocale, formatPhoneNumber, getDateByTimezone } from '@src/utils/utils'

import type { OrderItem, OrderItemTracking } from '@src/type/order'

import useGetVendorOrderItemsQuery from '../queries/order/useGetVendorOrderItemsQuery'
import useUpsertOrderItemTrackingMutate from '../queries/order/useUpsertOrderItemTrackingMutate'

type UpdatedTackingNoType = {
	[k: string]: string
}

type ExcelDataType = {
	orderDate: string
	orderNo: string
	nickname: string
	recipient: string
	phone: string | null
	productName: string
	skuName: string
	quantity: number
	courierName: string
	trackingNo: string
	address: string
	shippingAction: string
	totalPrice: string
}

const useOrderList = (itemPerPage: number, currentPage: number) => {
	const [vendorInfo] = useRecoilState(vendorAtom)
	const [orderItemList, setOrderItemList] = useState<OrderItem[]>([])
	const [updatedTrackingNo, setUpdatedTrackingNo] = useState<UpdatedTackingNoType>({})

	const offset = currentPage - 1 > 0 ? (currentPage - 1) * itemPerPage : 0

	const orderItemPayload = useMemo(
		() => ({
			status: 'published',
			showTracking: true,
			showOrder: true,
			showOrderShipping: true,
			showOrderTossRecord: true,
			showUser: true,
			showOrderItemTracking: true,
			limit: itemPerPage,
			offset,
		}),
		[itemPerPage, offset],
	)

	const {
		data: orderItemData,
		refetch,
		isLoading,
	} = useGetVendorOrderItemsQuery(orderItemPayload, {
		enabled: orderItemList.length === 0,
		retry: false,
		cacheTime: 0,
		keepPreviousData: true,
	})
	const { mutate: upsertTrackingMutate } = useUpsertOrderItemTrackingMutate()

	const handleChangeTackingNoByOrderItem = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>, orderItemNo: string) => {
			const { value } = e.target

			setUpdatedTrackingNo((updatedItem) => ({ ...updatedItem, [orderItemNo]: value }))
		},
		[],
	)

	const handleSetTackingNoByItemNo = useCallback((orderItemTracking: OrderItemTracking) => {
		const orderItemNo = orderItemTracking.order_item_no
		setOrderItemList((orderItems) =>
			orderItems.map((item) => {
				if (item.no === orderItemNo) {
					const newItem = { ...item, order_item_tracking: orderItemTracking }
					return newItem
				}
				return item
			}),
		)
	}, [])

	const handleRemoveUpdatedTrackingNo = (itemNo: string) => {
		setUpdatedTrackingNo((updatedItem) => {
			return Object.fromEntries(
				Object.entries(updatedItem).filter(([key]) => !key.includes(itemNo)),
			)
		})
	}

	const handleClickTrackingNoSave = useCallback(
		(itemNo: string) => {
			const currentTrackingNo = updatedTrackingNo?.[itemNo]
			try {
				upsertTrackingMutate(
					{ itemNo, trackingNo: currentTrackingNo ?? null },
					{
						onSuccess: (data) => {
							const orderItemTracking = data.data.upserted_item
							handleSetTackingNoByItemNo(orderItemTracking)
							handleRemoveUpdatedTrackingNo(itemNo)
						},
					},
				)
			} catch (error) {
				console.log(error)
			}
		},
		[handleSetTackingNoByItemNo, updatedTrackingNo, upsertTrackingMutate],
	)

	const handleClickTrackingNoModify = useCallback((itemNo: string) => {
		setOrderItemList((orderItems) =>
			orderItems.map((item) => {
				const newItem = item
				if (item.no === itemNo) {
					newItem.order_item_tracking.tracking_no = null
				}
				return newItem
			}),
		)
	}, [])

	const getExcelData = useMemo(() => {
		return orderItemList.reduce((acc: ExcelDataType[], curr) => {
			if (!curr.order) {
				return [...acc]
			}

			const orderItem: ExcelDataType = {
				orderDate: getDateByTimezone('Asia/Seoul', 'yyyy-MM-dd HH:mm:dd', curr.order.create_date),
				orderNo: curr.order_no,
				nickname: curr.order.user.nickname,
				recipient: curr.order.order_shipping.recipient,
				phone: formatPhoneNumber(curr.order.order_shipping.phone),
				productName: curr.archived_product.title,
				skuName: curr.archived_sku.name ?? '-',
				quantity: curr.quantity,
				courierName: curr.order_item_tracking?.courier_name ?? '-',
				trackingNo: curr.order_item_tracking?.tracking_no ?? '-',
				address: `${curr.order.order_shipping.address1} / ${curr.order.order_shipping.address2}`,
				shippingAction: curr.order.order_shipping.shipping_action,
				totalPrice: formatPriceByLocale(curr.order.total_price, 'ko'),
			}
			acc.push(orderItem)
			return acc
		}, [])
	}, [orderItemList])

	const handleClickDownload = useCallback(() => {
		const headerTitle = [
			'주문일시',
			'주문번호',
			'주문자',
			'수령인',
			'수령인 연락처',
			'상품명',
			'옵션',
			'수량',
			'택배사',
			'운송번호',
			'배송지 / 상세주소',
			'배송 메세지',
			'결제금액',
		]
		const excelData: ExcelDataType[] = getExcelData

		const workBook = xlsx.utils.book_new()
		const workSheet = xlsx.utils.json_to_sheet(excelData)
		const wscols = [
			{ wpx: 120 },
			{ wpx: 132 },
			{ wpx: 81 },
			{ wpx: 81 },
			{ wpx: 110 },
			{ wpx: 200 },
			{ wpx: 88 },
			{ wpx: 40 },
			{ wpx: 80 },
			{ wpx: 120 },
			{ wpx: 285 },
			{ wpx: 150 },
			{ wpx: 70 },
		]
		workSheet['!cols'] = wscols
		xlsx.utils.sheet_add_aoa(workSheet, [headerTitle])
		xlsx.utils.sheet_add_json(workSheet, [], { origin: 'A2', skipHeader: true })
		xlsx.utils.book_append_sheet(workBook, workSheet, '주문 내역')

		xlsx.writeFile(
			workBook,
			`${getDateByTimezone('Asia/Seoul', 'yyyy-MM-dd')}_${vendorInfo.brandName}_주문내역.xlsx`,
			{
				bookType: 'xlsx',
			},
		)
	}, [getExcelData, vendorInfo])

	useEffect(() => {
		if (isLoading && !orderItemData) return
		if (orderItemData?.data.order_items) {
			const { order_items: orderItems } = orderItemData.data
			setOrderItemList(orderItems)
		}
	}, [isLoading, orderItemData])

	useEffect(() => {
		if (currentPage > 0) {
			refetch().catch((error) => console.log(error))
		}
	}, [currentPage, refetch])

	return {
		orderItemList,
		handleChangeTackingNoByOrderItem,
		handleClickTrackingNoSave,
		handleClickTrackingNoModify,
		handleClickDownload,
	}
}
export default useOrderList
